// @givmo/compliance — removing a person's sign-in details from the organization's signing records, at
// its direction (CCM-R1-SIGN-REDACT (web); backend `POST …/signing-requests/{id}/identity-removals`).
//
// From a completed request's signing record, a sender (`compliance:send_for_signature`) picks one of
// the people it holds sign-in details for and says why; Givmo removes that person's sign-in email,
// IP address and browser from every signing record the organization holds, and each record then
// says so in its own lines (the cards re-read them). The server decides who may and when; a
// refusal shows in its own words.
//
// Whom it offers (CCM-R1-SIGN-REDACT-EARLIER-SIGNERS (web)): everyone the record holds them for, as
// the read serves them (`people`): its signers, someone whose acts are all on an earlier version
// (one who declined and was replaced), and a sender who never signed, each but a standing signer
// with the record's own line about them. A backend that doesn't serve `people` gets today's list,
// the live version's signers. Someone is chosen in advance only when the list holds one person:
// the act can't be undone. The Settings page's list of everyone the organization's requests hold
// details for (CCM-R1-SIGN-REDACT-UNFINISHED-REQUESTS (web)) opens the same dialog for one person.
//
// How it names them: by the host's roster (`organizationMembers`), as the Team page names people
// (their name, else their email), with the title the organization stated; only someone the roster
// doesn't list is "no longer in your organization" (cut 1008-105's review, F2). Until the roster
// is read it names nobody, and where it can't be read it says so.
//
// Words: CCM-R1-SIGN-REDACT's W1–W9 with W1-b, W4-a and W8-b, APPROVED (D-CCM-113, Jonathan
// 2026-10-21 16:46 PDT, "yes and approve rec"): every line as the review page drafts it, W6 as
// folded. W4-b, W8-c and W8-d–W8-f, APPROVED (D-CCM-115, Jonathan 2026-10-22 07:16 PDT,
// "approve"): every line as cut 1008-105's review page drafts it. A person's second line is the
// record's own, served: D-CCM-23's act headings and D-CCM-22's sent line, both APPROVED. The
// refusals the server words, N1–N3, are the backend's.

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  ErrorMessage,
  RadioTile,
  RadioTileGroup,
  Skeleton,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { useCompliance } from './compliance-context';
import { serverMessage } from './compliance-decode';
import type {
  SigningPerson,
  SigningRecordPerson,
  SigningRequestDetail,
} from './compliance-signing-decode';
import { signingPeopleQueryKey, signingRecordsQueryKey } from './compliance-queries';

/** W1, the record card's action. */
export const REMOVE_SIGN_IN_DETAILS = 'Remove a signer’s sign-in details…';
/** W1-b, the record card's menu's name. */
export const MORE_FOR_THIS_RECORD = 'More for this signing record';
/** W2, the dialog's title. */
export const REMOVE_SIGN_IN_DETAILS_TITLE = 'Remove a signer’s sign-in details';
/** W3, the dialog's lead. */
export const REMOVE_SIGN_IN_DETAILS_LEAD =
  'Givmo deletes the signer’s sign-in email, IP address and browser from every signing record your organization has in Givmo. Their signatures and the signed forms stay as they are, and each record says the details were removed.';
/** W4, the choice of signer. */
export const WHOSE_DETAILS = 'Whose details?';
/** W4-a, a signer no longer in the organization, by the title the organization stated. */
export const NO_LONGER_IN_YOUR_ORGANIZATION = (title: string) =>
  `${title}, no longer in your organization`;
/**
 * W4-b, a person the organization's roster no longer lists, who stated no title and whose name
 * Givmo didn't keep (a sender whose account had no name).
 */
export const SOMEONE_NO_LONGER_IN_YOUR_ORGANIZATION = 'Someone no longer in your organization';
/** W5, the stated basis. */
export const WHY = 'Why?';
export const BASIS_PERSON_ASKED = 'They asked us to delete their personal information';
export const BASIS_ORGANIZATION_DECIDED = 'Our organization decided to remove them';
/** W6, what it can't reach. */
export const REMOVAL_LIMITS =
  'This can’t be undone. Copies people already downloaded aren’t changed. Until they expire, Givmo’s backups and its recovery copies of deleted files keep these details, and its request logs keep the IP address and browser.';
/** W7, the dialog's two buttons. */
export const REMOVE_DETAILS = 'Remove details';
export const KEEP_THEM = 'Keep them';
/** W8, the confirmation. */
export const removedToast = (name: string, records: number) =>
  records === 1
    ? `Removed ${name}’s sign-in details from 1 signing record.`
    : `Removed ${name}’s sign-in details from ${records} signing records.`;
/**
 * W8-c, the details were removed, but no signing record held them, only requests that never
 * completed (CCM-R1-SIGN-REDACT-UNFINISHED-REQUESTS).
 */
export const removedFromRequestsToast = (name: string) =>
  `Removed ${name}’s sign-in details from your organization’s signing requests.`;
/** W8-b, the same direction again: the details were already removed. */
export const alreadyRemovedToast = (name: string, day: string) =>
  `${name}’s sign-in details were already removed on ${day}.`;
/**
 * W8-d, W8 for W4-b's person (cut 1008-105's review, F8), whom nothing names, so the confirmation
 * never calls them someone in the organization.
 */
export const removedSomeoneToast = (records: number) =>
  records === 1
    ? 'Removed the sign-in details of someone no longer in your organization from 1 signing record.'
    : `Removed the sign-in details of someone no longer in your organization from ${records} signing records.`;
/** W8-e, W8-b for W4-b's person. */
export const alreadyRemovedSomeoneToast = (day: string) =>
  `The sign-in details of someone no longer in your organization were already removed on ${day}.`;
/** W8-f, W8-c for W4-b's person. */
export const REMOVED_SOMEONE_FROM_REQUESTS =
  'Removed the sign-in details of someone no longer in your organization from its signing requests.';
/** W9, the removal failed with no words of its own (the server words every refusal it makes). */
export const REMOVAL_FAILED = 'We couldn’t remove those details. Please try again.';

type Basis = 'person_asked' | 'organization_decided';

/** The organization's roster by user id, each named as its Team page names them. */
export type OrganizationRoster = ReadonlyMap<string, string>;

/**
 * The host's roster as the removal reads it: `undefined` while it reads, `null` when it couldn't be
 * read, else each member by user id.
 */
export function rosterOf(
  members: readonly { userId: string; name: string }[] | null | undefined,
): OrganizationRoster | null | undefined {
  if (members === undefined || members === null) return members;
  return new Map(members.map((member) => [member.userId, member.name]));
}

/**
 * One person the dialog can name: its tile, the tile's second line, and how the confirmation says
 * them; `unnamed` for W4-b's person, whom the confirmations name in their own words (W8-d–W8-f).
 */
export interface RemovalPerson {
  userId: string;
  label: string;
  description?: string;
  name: string;
  unnamed?: true;
}

/**
 * A person as the console names them. Someone the roster lists: their roster name (the Team page's,
 * their name else their email), with their title (W4). Someone it doesn't list is no longer in the
 * organization: W4-a by their title, else the name Givmo kept when they sent the request, else W4-b.
 */
export function namedPerson(
  person: { userId: string; capacityText?: string; sentAs?: string },
  roster: OrganizationRoster,
): Pick<RemovalPerson, 'label' | 'name' | 'unnamed'> {
  const name = roster.get(person.userId);
  const title = person.capacityText;
  if (name !== undefined) return { label: title !== undefined ? `${name}, ${title}` : name, name };
  if (title !== undefined) return { label: NO_LONGER_IN_YOUR_ORGANIZATION(title), name: title };
  if (person.sentAs !== undefined) return { label: person.sentAs, name: person.sentAs };
  return { label: SOMEONE_NO_LONGER_IN_YOUR_ORGANIZATION, name: '', unnamed: true };
}

/** The live version's signers, one each: the list where the read serves no `people`. */
export function recordSigners(
  detail: SigningRequestDetail,
  roster: OrganizationRoster,
): RemovalPerson[] {
  const seen = new Map<string, RemovalPerson>();
  for (const slot of detail.slots) {
    if (seen.has(slot.signerUserId)) continue;
    seen.set(slot.signerUserId, {
      userId: slot.signerUserId,
      ...namedPerson({ userId: slot.signerUserId, capacityText: slot.capacityText }, roster),
    });
  }
  return [...seen.values()];
}

/** Everyone the record holds sign-in details for, as served, each but a standing signer with its line. */
export function recordPeople(
  detail: SigningRequestDetail,
  roster: OrganizationRoster,
): RemovalPerson[] {
  if (detail.people.length === 0) return recordSigners(detail, roster);
  return detail.people.map((person: SigningRecordPerson) => ({
    userId: person.userId,
    ...namedPerson(person, roster),
    ...(person.line !== undefined ? { description: person.line } : {}),
  }));
}

/** The people as the dialog waits for the roster: `undefined` while it reads, `null` if it can't. */
function peopleOnceNamed(
  roster: OrganizationRoster | null | undefined,
  name: (roster: OrganizationRoster) => RemovalPerson[],
): RemovalPerson[] | null | undefined {
  if (roster === undefined || roster === null) return roster;
  return name(roster);
}

/** From a completed request's signing record: everyone it holds sign-in details for. */
export function RemoveSignInDetailsDialog({
  detail,
  open,
  onClose,
}: {
  detail: SigningRequestDetail;
  open: boolean;
  onClose: () => void;
}) {
  const { organizationMembers } = useCompliance();
  return (
    <RemovalDialog
      requestId={detail.request.requestId}
      people={peopleOnceNamed(rosterOf(organizationMembers), (roster) =>
        recordPeople(detail, roster),
      )}
      timeZone={detail.version.timeZone}
      open={open}
      onClose={onClose}
    />
  );
}

/**
 * From the Settings page's list (CCM-R1-SIGN-REDACT-UNFINISHED-REQUESTS (web)): one person, chosen,
 * directed on the request the read names for them. W8-b's day is the viewer's, with no record
 * whose zone to write it in.
 */
export function RemovePersonSignInDetailsDialog({
  person,
  open,
  onClose,
}: {
  person: SigningPerson;
  open: boolean;
  onClose: () => void;
}) {
  const { organizationMembers } = useCompliance();
  return (
    <RemovalDialog
      requestId={person.requestId}
      people={peopleOnceNamed(rosterOf(organizationMembers), (roster) => [
        { userId: person.userId, ...namedPerson(person, roster) },
      ])}
      timeZone={undefined}
      open={open}
      onClose={onClose}
    />
  );
}

/** The confirmation: W8, W8-b or W8-c by the person's name, or W8-d–W8-f for W4-b's person. */
function confirmation(
  person: RemovalPerson | undefined,
  { records, removedNow, day }: { records: number; removedNow: boolean; day: string },
): string {
  if (person?.unnamed === true) {
    if (!removedNow) return alreadyRemovedSomeoneToast(day);
    return records > 0 ? removedSomeoneToast(records) : REMOVED_SOMEONE_FROM_REQUESTS;
  }
  const name = person?.name ?? '';
  if (!removedNow) return alreadyRemovedToast(name, day);
  return records > 0 ? removedToast(name, records) : removedFromRequestsToast(name);
}

function RemovalDialog({
  requestId,
  people,
  timeZone,
  open,
  onClose,
}: {
  requestId: string;
  /** `undefined` while the roster reads, `null` when it couldn't be read. */
  people: RemovalPerson[] | null | undefined;
  timeZone: string | undefined;
  open: boolean;
  onClose: () => void;
}) {
  const { orgRef, operations } = useCompliance();
  const queryClient = useQueryClient();
  // Controlled from the first render: '' is no choice yet, and a list of several waits for one. A
  // list of one comes chosen, once it is named.
  const [tapped, setTapped] = useState<string | undefined>(undefined);
  const only =
    people !== undefined && people !== null && people.length === 1 ? people[0] : undefined;
  const signer = tapped ?? only?.userId ?? '';
  const [basis, setBasis] = useState<Basis | ''>('');
  const remove = useMutation({
    // Sends exactly the signer and the reason picked: a stated basis is never defaulted.
    mutationFn: async (picked: { signerUserId: string; basis: Basis }) => {
      const answer = (await operations.removeSigningIdentity(orgRef ?? '', requestId, {
        signer_user_id: picked.signerUserId,
        basis: picked.basis,
      })) as
        | { signing_records?: unknown; removed_now?: unknown; directed_at?: unknown }
        | undefined;
      return {
        records: typeof answer?.signing_records === 'number' ? answer.signing_records : 0,
        removedNow: answer?.removed_now !== false,
        directedAt: typeof answer?.directed_at === 'string' ? answer.directed_at : undefined,
      };
    },
    onSuccess: async ({ records, removedNow, directedAt }, chosen) => {
      const person = people?.find((each) => each.userId === chosen.signerUserId);
      const day =
        directedAt !== undefined
          ? new Date(directedAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              ...(timeZone !== undefined ? { timeZone } : {}),
            })
          : '';
      toast.success(confirmation(person, { records, removedNow, day }));
      // Every record holding the person was written again, and the list of people may lose them.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: signingRecordsQueryKey(orgRef ?? null) }),
        queryClient.invalidateQueries({ queryKey: signingPeopleQueryKey(orgRef ?? null) }),
      ]);
      onClose();
    },
  });
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !remove.isPending) onClose();
      }}
    >
      {open && (
        <Dialog.Content
          width={480}
          className="flex flex-col gap-16"
          testId="compliance-remove-sign-in-details"
        >
          <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            {REMOVE_SIGN_IN_DETAILS_TITLE}
          </Dialog.Title>
          <Dialog.Description className="text-s14 text-text-secondary">
            {REMOVE_SIGN_IN_DETAILS_LEAD}
          </Dialog.Description>
          <fieldset className="flex flex-col gap-8">
            <legend className="mb-6 text-s13 font-w600 text-text-strong">{WHOSE_DETAILS}</legend>
            {people === undefined ? (
              <Skeleton className="h-[72px] w-full" testId="compliance-remove-signer-loading" />
            ) : people === null ? (
              <ErrorMessage testId="compliance-remove-signer-read-error">
                Please try again.
              </ErrorMessage>
            ) : (
              <RadioTileGroup
                value={signer}
                onValueChange={setTapped}
                aria-label={WHOSE_DETAILS}
                testId="compliance-remove-signer"
              >
                {people.map((each) => (
                  <RadioTile
                    key={each.userId}
                    value={each.userId}
                    label={each.label}
                    {...(each.description !== undefined ? { description: each.description } : {})}
                    testId={`compliance-remove-signer-${each.userId}`}
                  />
                ))}
              </RadioTileGroup>
            )}
          </fieldset>
          <fieldset className="flex flex-col gap-8">
            <legend className="mb-6 text-s13 font-w600 text-text-strong">{WHY}</legend>
            <RadioTileGroup
              value={basis}
              onValueChange={(value) => setBasis(value as Basis)}
              aria-label={WHY}
              testId="compliance-remove-basis"
            >
              <RadioTile
                value="person_asked"
                label={BASIS_PERSON_ASKED}
                testId="compliance-remove-basis-asked"
              />
              <RadioTile
                value="organization_decided"
                label={BASIS_ORGANIZATION_DECIDED}
                testId="compliance-remove-basis-decided"
              />
            </RadioTileGroup>
          </fieldset>
          <p className="text-s13 text-text-tertiary" {...testIdProp('compliance-remove-limits')}>
            {REMOVAL_LIMITS}
          </p>
          {remove.error !== null && (
            <ErrorMessage testId="compliance-remove-sign-in-details-failure">
              {serverMessage(remove.error, REMOVAL_FAILED)}
            </ErrorMessage>
          )}
          <div className="flex justify-end gap-10">
            <Button
              appearance="saas"
              tone="secondary"
              type="button"
              onClick={onClose}
              disabled={remove.isPending}
              testId="compliance-remove-sign-in-details-keep"
            >
              {KEEP_THEM}
            </Button>
            <Button
              appearance="saas"
              tone="danger"
              type="button"
              loading={remove.isPending}
              disabled={signer === '' || basis === ''}
              onClick={() => {
                if (signer !== '' && basis !== '') remove.mutate({ signerUserId: signer, basis });
              }}
              testId="compliance-remove-sign-in-details-confirm"
            >
              {REMOVE_DETAILS}
            </Button>
          </div>
        </Dialog.Content>
      )}
    </Dialog.Root>
  );
}
